import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';
import { supabase } from '../lib/supabase';
import { Customer } from '../types/crm';
import { mapLocationDbRow } from '../utils/locationDbRow';

interface ClientState {
  customer: Customer | null;
  loading: boolean;
}

export function useClient() {
  const { user } = useAuth();
  const [clientState, setClientState] = useState<ClientState>({
    customer: null,
    loading: true,
  });

  useEffect(() => {
    if (!user?.id) {
      setClientState({ customer: null, loading: false });
      return;
    }

    const checkClientStatus = async () => {
      try {
        const { data, error } = await supabase
          .from('customers')
          .select(`
            id,
            company_name,
            industry,
            general_notes,
            user_id,
            locations(*),
            contacts(*)
          `)
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error checking client status:', error);
          setClientState({ customer: null, loading: false });
          return;
        }

        if (data) {
          const customer: Customer = {
            id: data.id,
            companyName: data.company_name,
            industry: data.industry,
            generalNotes: data.general_notes,
            userId: data.user_id,
            locations: (data.locations || []).map((loc: any) => mapLocationDbRow(loc)),
            contacts: (data.contacts || []).map((contact: any) => ({
              id: contact.id,
              firstName: contact.first_name,
              lastName: contact.last_name,
              role: contact.role,
              email: contact.email,
              phoneNumber: contact.phone_number,
              isBillingContact: contact.is_billing_contact,
              isGeneralContact: contact.is_general_contact,
              contactNotes: contact.contact_notes
            })),
            numCertsGenerated: 0,
            isPaid: false,
            lastPaymentDate: '',
            paymentHistory: [],
            currentBatchCertificates: [],
            generatedCertificates: []
          };
          setClientState({ customer, loading: false });
        } else {
          setClientState({ customer: null, loading: false });
        }
      } catch (error) {
        console.error('Error checking client status:', error);
        setClientState({ customer: null, loading: false });
      }
    };

    checkClientStatus();
  }, [user]);

  return clientState;
}
